"use client";
import React, { useState } from "react";
import { FiCheck } from "react-icons/fi";
import { API_BASE } from "../config";
import ErrorMessage from "../components/ErrorMessage";
import SuccessMessage from "../components/SuccessMessage";

export default function RequestPage() {
  const navy = "#0E2B55", cyan = "#2BB0F6", blue = "#3366CC", text = "#0F172A";
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });

  const pageStyle = {
    width: "100%",
    minHeight: "100vh",
    fontFamily: "Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, 'Helvetica Neue', Arial",
    color: text,
    background: "#F5F7FA",
    overflowX: "hidden",
  };

  const fieldBase = {
    width: "100%", height: 40, paddingLeft: 12, paddingRight: 12,
    fontSize: 14, borderRadius: 8, border: "1px solid #C7CED6",
    background: "#fff", color: "#111827", outline: "none", boxSizing: "border-box",
  };

  const labelStyle = { display: "block", fontSize: 13, fontWeight: 700, color: "#E7EEF6", marginBottom: 6 };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    const formEl = e.currentTarget;
    const form = new FormData(formEl);
    const payload = {
      customer_name: `${form.get("firstName")} ${form.get("lastName")}`,
      customer_email: form.get("email"),
      company: form.get("company"),
      phone: form.get("phone"),
      items: [{ sku: "PURCHASE_REQUEST", name: "Purchase Order Request", qty: 1, price: 0 }],
      grand_total: 0,
      note: `Job Title: ${form.get("jobTitle")}\nMessage: ${form.get("message")}`,
      form_type: "Purchase",
    };
    try {
      setLoading(true);
      setMsg({ type: "", text: "" });
      const res = await fetch(`${API_BASE}/orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Submission failed");
      setMsg({ type: "success", text: "Purchase request sent! Our team will contact you shortly." });
      formEl.reset();
      setTimeout(() => setMsg({ type: "", text: "" }), 6000);
    } catch (err) {
      setMsg({ type: "error", text: "Failed to submit. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={pageStyle}>
      <style>{`
        .container { max-width: 1200px; margin: 0 auto; padding: 0 20px; }
        .hero-section { background: ${navy}; color: #fff; padding: 40px 0; text-align: center; }
        .main-layout { display: grid; grid-template-columns: 1.2fr 1fr; gap: 60px; padding: 60px 0; align-items: start; }
        .form-card { background: #0C2447; border-radius: 16px; padding: 30px; box-shadow: 0 20px 40px rgba(0,0,0,0.2); width: 100%; box-sizing: border-box; }
        .input-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 16px; }
        .gradient-text {
          background: linear-gradient(90deg, ${blue}, ${cyan});
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          display: inline-block;
        }
        .check-list { list-style: none; padding: 0; }
        .check-list li { position: relative; padding-left: 30px; marginBottom: 15px; line-height: 1.6; display: flex; align-items: flex-start; gap: 10px; }
        .check-list li .check-icon { flex-shrink: 0; margin-top: 2px; }
        
        @media (max-width: 991px) {
          .main-layout { grid-template-columns: 1fr; gap: 40px; padding: 40px 20px; }
        }
        @media (max-width: 600px) {
          .input-row { grid-template-columns: 1fr; }
        }
      `}</style>

      {/* Header */}
      <section className="hero-section">
        <div className="container">
          <div style={{ fontSize: 48, fontWeight: 900, color: cyan, letterSpacing: "1px" }}>SMART AUDIT</div>
        </div>
      </section>

      {/* Enhanced Headline Section */}
      <div style={{ padding: "60px 20px 0", textAlign: "center" }}>
        <div style={{
          display: "inline-block", padding: "6px 16px", background: "rgba(43, 176, 246, 0.1)",
          color: cyan, borderRadius: "20px", fontSize: "12px", fontWeight: "bold",
          letterSpacing: "1px", marginBottom: "16px", border: `1px solid ${cyan}`
        }}>
          TRUSTED LICENSE SECURITY
        </div>
        <h1 style={{ 
          margin: "0 auto", maxWidth: "800px", color: "#0F172A", fontWeight: 900, 
          fontSize: "clamp(28px, 6vw, 48px)", lineHeight: 1.1, letterSpacing: "-0.02em" 
        }}>
          Enhance your License Security with <br/>
          <span className="gradient-text">Smart Audit.</span>
        </h1>
        <p style={{ marginTop: "20px", color: "#64748B", fontSize: "clamp(16px, 2vw, 18px)", maxWidth: "600px", margin: "20px auto 0", lineHeight: 1.6 }}>
          Protect your software assets and ensure compliance with our all-in-one activation and monitoring solution.
        </p>
      </div>

      <div className="container">
        <main className="main-layout">
          
          {/* Left Content */}
          <div style={{ color: text }}>
            <h3 style={{ fontSize: "24px", marginBottom: "20px", fontWeight: 700 }}>Why Choose Smart Audit?</h3>
            <ul className="check-list">
              <li>
                <FiCheck className="check-icon" size={18} style={{ color: cyan }} />
                <span><strong style={{ color: blue }}>Advanced Protection:</strong> 
                Ensure only authorized users can activate and use your software.</span>
              </li>
              <li>
                <FiCheck className="check-icon" size={18} style={{ color: cyan }} />
                <span><strong style={{ color: blue }}>Flexible Deployment:</strong> 
                Seamlessly integrate via SaaS or On-prem options.</span>
              </li>
              <li>
                <FiCheck className="check-icon" size={18} style={{ color: cyan }} />
                <span><strong style={{ color: blue }}>Real-time Monitoring:</strong> 
                Full visibility of your licensing environment in one dashboard.</span>
              </li>
            </ul>
            <div style={{ fontSize: "13px", color: "#94A3B8", fontStyle: "italic", marginTop: "30px", padding: "15px", borderLeft: `4px solid ${cyan}`, background: "#fff" }}>
              * Minimum one-year engagement required for enterprise solutions.
            </div>
          </div>

          {/* Right Content (Form) */}
          <div className="form-card">
            <h2 style={{ color: "#fff", textAlign: "center", marginBottom: "25px", fontSize: "20px", fontWeight: 700 }}>Fill our form now</h2>

            {msg.type === "error" && <ErrorMessage message={msg.text} style={{ marginBottom: 20 }} />}
            {msg.type === "success" && <SuccessMessage message={msg.text} style={{ marginBottom: 20 }} />}

            <form onSubmit={onSubmit}>
              <div className="input-row">
                <div><label style={labelStyle}>First Name*</label><input name="firstName" placeholder="First Name" required maxLength={50} style={fieldBase} /></div>
                <div><label style={labelStyle}>Last Name*</label><input name="lastName" placeholder="Last Name" required maxLength={50} style={fieldBase} /></div>
              </div>
              <div className="input-row">
                <div><label style={labelStyle}>Business Email*</label><input type="email" name="email" placeholder="email@company.com" required maxLength={254} style={fieldBase} /></div>
                <div><label style={labelStyle}>Phone Number*</label><input name="phone" placeholder="Phone Number" required maxLength={20} style={fieldBase} /></div>
              </div>
              <div className="input-row">
                <div><label style={labelStyle}>Job Title*</label><input name="jobTitle" placeholder="Job Title" required maxLength={80} style={fieldBase} /></div>
                <div><label style={labelStyle}>Company Name*</label><input name="company" placeholder="Company Name" required maxLength={100} style={fieldBase} /></div>
              </div>
              <div style={{ marginBottom: "20px" }}>
                <label style={labelStyle}>Message*</label>
                <textarea name="message" placeholder="How can we help you?" required rows={4} maxLength={500} style={{ ...fieldBase, height: "auto", paddingTop: "10px", resize: "none" }} />
              </div>

              {/* แก้ไข Warning: เปลี่ยนจาก <a> เป็น <span> ที่มีสไตล์ลิงก์ */}
              <p style={{ fontSize: "11px", color: "#94A3B8", lineHeight: "1.5", marginBottom: "20px" }}>
                Smart Click needs the contact information you provide to us to contact you... 
                <span style={{ color: cyan, marginLeft: "4px", cursor: "pointer", textDecoration: "underline" }} onClick={() => window.open('/privacy-policy', '_blank')}>
                  Privacy Policy
                </span>.
              </p>

              <button 
                type="submit" 
                disabled={loading}
                aria-label={loading ? "Submitting form, please wait" : "Submit form"}
                style={{
                  width: "100%", 
                  height: "48px", 
                  background: blue, 
                  color: "#fff", 
                  border: "none",
                  borderRadius: "8px", 
                  fontWeight: "bold", 
                  fontSize: "16px", 
                  cursor: loading ? "not-allowed" : "pointer",
                  transition: "all 0.3s ease", 
                  opacity: loading ? 0.7 : 1
                }}
              >
                {loading ? "Submitting..." : "Submit Form"}
              </button>
            </form>
          </div>
        </main>
      </div>
    </div>
  );
}